import { Group, Select, Text } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerSafeTicketDetail } from "@msc/shared";

import { isJiraReauthRequired } from "../../hooks/useCustomerView.js";
import { useAssignableSearch, useSetAssignee, useSetDueDate, useSetPriority } from "../../hooks/useCustomerViewActions.js";
import { writeErrorText } from "../../lib/customerView/jiraValidation.js";
import { priorityLabel } from "../../lib/customerView/priorityLabel.js";
import { fromLocalDateString, toLocalDateString } from "../../lib/date.js";
import { ReconnectBanner } from "./ReconnectBanner.js";

const NO_ASSIGNEE = "__none__";

/**
 * Priorität, Fälligkeit, Bearbeiter — jede Änderung geht sofort nach Jira; der
 * Wert kommt aus dem neu geladenen Detail zurück.
 *
 * `Autocomplete` trägt in dieser Mantine-Version nur String-Daten
 * (`ComboboxStringItem` hat kein `label`) — für den Bearbeiter brauchen wir
 * aber Anzeigename (Label) getrennt von der Account-Id (Wert). Deshalb ein
 * durchsuchbares `Select` mit kontrolliertem `searchValue`: Getippt wird die
 * Server-Suche gefüttert, die Auswahl liefert die Id.
 */
export function TicketMetaEditors({ detail, objectKey }: { detail: CustomerSafeTicketDetail; objectKey: string | null }) {
  const { t } = useTranslation();
  const setPriority = useSetPriority(objectKey);
  const setDueDate = useSetDueDate(objectKey);
  const setAssignee = useSetAssignee(objectKey);
  const [assigneeQuery, setAssigneeQuery] = useState("");
  const assignable = useAssignableSearch(detail.key, assigneeQuery);
  const busy = setPriority.isPending || setDueDate.isPending || setAssignee.isPending;
  const writeError = setPriority.error ?? setDueDate.error ?? setAssignee.error;
  const failed = setPriority.isError || setDueDate.isError || setAssignee.isError;
  const reauth = failed && isJiraReauthRequired(writeError);

  const assigneeData = [
    ...(assignable.data?.users ?? []).map((u) => ({ value: u.accountId, label: u.displayName })),
    { value: NO_ASSIGNEE, label: t("pages.customerView.cockpit.actions.assigneeNone") },
  ];

  return (
    <Group gap="md" align="flex-end" wrap="wrap">
      <Select
        size="xs" w={140}
        label={t("pages.customerView.cockpit.actions.priority")}
        value={detail.priority?.id ?? null}
        data={detail.priorityOptions.map((p) => ({ value: p.id, label: priorityLabel(p.name) }))}
        onChange={(v) => { if (v) setPriority.mutate({ key: detail.key, priorityId: v }); }}
        disabled={busy}
      />
      <DateInput
        size="xs" w={160}
        label={t("pages.customerView.cockpit.actions.dueDate")}
        value={detail.dueDate ? fromLocalDateString(detail.dueDate) : null}
        clearable
        clearButtonProps={{ "aria-label": t("pages.customerView.cockpit.actions.dueDateClear") }}
        onChange={(d) => setDueDate.mutate({ key: detail.key, dueDate: d ? toLocalDateString(d) : null })}
        disabled={busy}
      />
      <Select
        size="xs" w={220}
        label={t("pages.customerView.cockpit.actions.assignee")}
        placeholder={detail.assignee?.displayName ?? t("pages.customerView.cockpit.actions.assigneeNone")}
        searchable
        searchValue={assigneeQuery}
        onSearchChange={setAssigneeQuery}
        value={null}
        data={assigneeData}
        // Die Ergebnisse kommen bereits gefiltert vom Server (`useAssignableSearch`)
        // bzw. sind die feste „kein Bearbeiter"-Option — hier nicht nochmal
        // clientseitig gegen den Suchtext filtern, sonst verschwindet die
        // Option bei jeder Eingabe.
        filter={({ options }) => options}
        nothingFoundMessage={t("pages.customerView.cockpit.actions.assigneeSearch")}
        onChange={(value) => {
          if (!value) return;
          setAssignee.mutate({ key: detail.key, accountId: value === NO_ASSIGNEE ? null : value });
          setAssigneeQuery("");
        }}
        disabled={busy}
      />
      {reauth && <ReconnectBanner />}
      {failed && !reauth && (
        <Text size="xs" c="red">{writeErrorText(writeError, t, "pages.customerView.cockpit.actions.saveFailed")}</Text>
      )}
    </Group>
  );
}
